import React, { lazy, Suspense } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import {
  BackendHealthProvider,
  useBackendHealth,
} from "@/contexts/BackendHealthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteLoadingSkeleton from "@/components/RouteLoadingSkeleton";
import TeamTabLoadingScreen from "@/components/TeamTabLoadingScreen";
import Navigation from "@/components/Navigation";
import ErrorBoundary from "@/components/ErrorBoundary";
import { LegacyGroupRedirect } from "@/components/LegacyGroupRedirect";

// Authentication pages
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));

// Redirect component for login/register routes
function RedirectToSSO() {
  const [searchParams] = React.useState(
    new URLSearchParams(window.location.search),
  );
  const error = searchParams.get("error");

  React.useEffect(() => {
    // Only redirect if there is no error
    if (!error) {
      const BACKEND_URL =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
      window.location.href = `${BACKEND_URL}/api/scalekit/login`;
    }
  }, [error]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              Authentication Failed
            </h2>
            <p className="text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg font-mono text-sm break-words">
              {decodeURIComponent(error)}
            </p>
          </div>

          <div className="pt-2">
            <button
              onClick={() => {
                const BACKEND_URL =
                  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
                window.location.href = `${BACKEND_URL}/api/scalekit/login`;
              }}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-colors"
            >
              Try Logging In Again
            </button>
          </div>

          <div className="text-xs text-slate-500">
            <a href="/" className="hover:underline">
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-lg text-slate-600 dark:text-slate-300 font-medium">
          Redirecting to login...
        </p>
      </div>
    </div>
  );
}

// Lazy load all page components for better performance
// Original routed pages (7)
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const TeammateLookup = lazy(() => import("@/pages/TeammateLookup"));
const TabDashboard = lazy(() => import("@/pages/TabDashboard"));
const PeriodDetail = lazy(() => import("@/pages/PeriodDetail"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const TeammatesList = lazy(() => import("@/pages/TeammatesList"));
const ActivitiesPage = lazy(() => import("@/pages/ActivitiesPage"));

// Additional pages - High Priority (Referenced in Navigation) (4)
const Signups = lazy(() => import("@/pages/Signups"));
const Split = lazy(() => import("@/pages/Split"));
const Collections = lazy(() => import("@/pages/Collections"));
const Analytics = lazy(() => import("@/pages/Analytics"));

// Additional pages - Medium Priority (Public/Support) (4)
const JoinTab = lazy(() => import("@/pages/JoinTab"));
const PublicSignup = lazy(() => import("@/pages/PublicSignup"));
const PeriodSignup = lazy(() => import("@/pages/PeriodSignup"));

// Member self-service portal
const TeammatePortalPage = lazy(() => import("@/pages/TeammatePortalPage"));

// Group management pages
const CreateTabPage = lazy(() => import("@/pages/CreateTabPage"));
const TabSettingsPage = lazy(() => import("@/pages/TabSettingsPage"));
const ShortLinkHandler = lazy(() => import("@/pages/ShortLinkHandler"));
const InviteHandler = lazy(() => import("@/pages/InviteHandler"));
const EscalationSettingsPage = lazy(
  () => import("@/pages/EscalationSettingsPage"),
);

// User profile page
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));

// Marketing pages
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));

// Legal pages
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));

// Payment pages
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentCancelPage"));

// Account linking page
const LinkAccountPage = lazy(() => import("@/pages/LinkAccountPage"));

const AppContent = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const showNav =
    (location.pathname !== "/" || isAuthenticated) &&
    !location.pathname.startsWith("/member/");

  return (
    <>
      {showNav && <Navigation />}
      <main id="main-content">
        <Suspense fallback={<RouteLoadingSkeleton />}>
          <Routes>
            {/* Authentication routes (public) */}
            <Route path="/login" element={<RedirectToSSO />} />
            <Route path="/register" element={<RedirectToSSO />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/reset-password/:token"
              element={<ResetPasswordPage />}
            />

            {/* Original routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/member-lookup" element={<TeammateLookup />} />

            {/* Member self-service portal (token-based, no auth required) */}
            <Route path="/member/:token" element={<TeammatePortalPage />} />

            {/* Short link handler (public) */}
            <Route path="/g/:shortCode" element={<ShortLinkHandler />} />
            {/* Legacy invite handler (redirects to short link) */}
            <Route path="/invite/:groupId" element={<InviteHandler />} />

            {/* New canonical tab routes */}
            <Route path="/tab/:groupId" element={<TabDashboard />} />
            <Route path="/tab/:groupId/members" element={<TeammatesList />} />
            <Route
              path="/tab/:groupId/activities"
              element={<ActivitiesPage />}
            />
            <Route
              path="/tab/:groupId/period/:periodId"
              element={<PeriodDetail />}
            />
            <Route
              path="/tab/:groupId/period/:periodId/signups"
              element={<Signups />}
            />

            {/* Protected tab admin routes */}
            <Route
              path="/tab/:groupId/admin"
              element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />

            {/* Protected tab settings route */}
            <Route
              path="/tab/:groupId/settings"
              element={
                <ProtectedRoute>
                  <TabSettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Protected escalation settings route */}
            <Route
              path="/group/:groupId/escalation-settings"
              element={
                <ProtectedRoute>
                  <EscalationSettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Protected analytics route (group-scoped) */}
            <Route
              path="/tab/:groupId/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />

            {/* Protected create tab route */}
            <Route
              path="/create-tab"
              element={
                <ProtectedRoute>
                  <CreateTabPage />
                </ProtectedRoute>
              }
            />

            {/* Protected profile route */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Legacy /group/* routes - redirect to /tab/* */}
            <Route path="/group/*" element={<LegacyGroupRedirect />} />
            <Route
              path="/create-group"
              element={<Navigate to="/create-tab" replace />}
            />

            {/* New routes - High Priority (Navigation) */}
            <Route path="/signups" element={<Signups />} />
            <Route path="/split" element={<Split />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/analytics" element={<Analytics />} />

            {/* New routes - Medium Priority (Public/Support) */}
            <Route path="/join" element={<JoinTab />} />
            <Route path="/signup" element={<PublicSignup />} />
            <Route path="/period-signup" element={<PeriodSignup />} />

            {/* Marketing pages */}
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Legal pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />

            {/* Payment pages */}
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />

            {/* Account linking (post OAuth callback) */}
            <Route path="/link-account" element={<LinkAccountPage />} />
          </Routes>
        </Suspense>
      </main>
    </>
  );
};

/**
 * ConditionalLoadingScreen - Shows loading screen while backend initializes
 */
const ConditionalLoadingScreen = ({ children }) => {
  const { isLoading } = useBackendHealth();

  // Show loading screen while backend is initializing or connecting
  if (isLoading) {
    return <TeamTabLoadingScreen />;
  }

  // Backend is ready, render app
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BackendHealthProvider>
          <ConditionalLoadingScreen>
            <AuthProvider>
              <NotificationProvider>
                <div className="App">
                  <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-brand focus:text-white focus:rounded-md"
                  >
                    Skip to main content
                  </a>
                  <BrowserRouter>
                    <AppContent />
                  </BrowserRouter>
                  <Toaster position="top-right" />
                </div>
              </NotificationProvider>
            </AuthProvider>
          </ConditionalLoadingScreen>
        </BackendHealthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
